import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, CheckCircle, Clock, FileText, User, CreditCard, Zap, RefreshCw, Upload, Search, Eye, CheckSquare, DollarSign, Archive, FolderOpen, File, Brain, Hand, Pause, MoreVertical, ChevronDown, ChevronRight, Sparkles, RotateCw, FileCheck } from 'lucide-react';
import { useBillQueue } from '@/hooks/useBillQueue';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useProcessingBridge } from '@/hooks/useProcessingBridge';
import { supabase } from '@/integrations/supabase/client';
import { AILearningFeedbackDialog } from '@/components/AILearningFeedbackDialog';
import { QueueAssignmentDialog } from '@/components/QueueAssignmentDialog';
import { useNavigate } from 'react-router-dom';
import { EnergyBillMetricsDashboard } from '@/components/EnergyBillMetricsDashboard';
import { ArchiveHierarchyView } from '@/components/ArchiveHierarchyView';
import { BillPreviewDrawer } from '@/components/BillPreviewDrawer';
import { formatDuration } from '@/lib/stage-utils';
import { ColumnSettings, type Column } from '@/components/tum/ColumnSettings';
import { ReparseDialog } from '@/components/stage/processing/ReparseDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ParsingMethodBadge, ParsingStatusBadge } from '@/components/stage/processing/ParsingBadges';
import { ExpandedRowContent } from '@/components/stage/processing/ExpandedRowContent';

interface EnhancedQueueDashboardProps {
  activeTab?: string;
}

export const EnhancedQueueDashboard = ({ activeTab = "intake" }: EnhancedQueueDashboardProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { 
    queueItems,
    intakeItems,
    dataExtractionItems,
    processingItems,
    manualReviewItems,
    validationItems,
    approvalItems,
    paymentReadyItems,
    archiveItems,
    isLoading, 
    updateQueueItem, 
    assignToBill,
    deleteQueueItem
  } = useBillQueue();
  
  const { processQueueItem, createBillFromQueue } = useProcessingBridge();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedbackDialog, setFeedbackDialog] = useState<{
    open: boolean;
    data?: any;
    billId?: string;
    queueItemId?: string;
  }>({ open: false });
  
  const [assignmentDialog, setAssignmentDialog] = useState<{
    open: boolean;
    queueItem?: any;
  }>({ open: false });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<any | null>(null);
  const [reparseDialog, setReparseDialog] = useState<{ open: boolean; billId?: string; data?: any }>({ open: false });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Column management state
  const [columns, setColumns] = useState<Column[]>([
    { id: 'checkbox', label: 'Select', visible: true, locked: true },
    { id: 'expand', label: '', visible: true, locked: true },
    { id: 'files', label: 'Files', visible: true },
    { id: 'bill_id', label: 'Bill ID', visible: true },
    { id: 'stage_age', label: 'Stage Age', visible: true },
    { id: 'sla_due', label: 'SLA Due', visible: true },
    { id: 'parsing_method', label: 'Parse Method', visible: true },
    { id: 'parsing_status', label: 'Parse Status', visible: true },
    { id: 'source', label: 'Source', visible: true },
    { id: 'customer', label: 'Customer', visible: true },
    { id: 'retailer', label: 'Retailer', visible: true },
    { id: 'account', label: 'Account/Site', visible: true },
    { id: 'period', label: 'Period', visible: false },
    { id: 'amount', label: 'Amount', visible: true },
    { id: 'confidence', label: 'Confidence', visible: true },
    { id: 'flags', label: 'Flags', visible: true },
    { id: 'assignee', label: 'Assignee', visible: false },
    { id: 'actions', label: 'Actions', visible: true, locked: true },
  ]);

  const isColumnVisible = (columnId: string) => {
    return columns.find(c => c.id === columnId)?.visible ?? false;
  };

  const getStageAge = (item: any) => {
    const entered = item.stage_entered_at || item.created_at;
    if (!entered) return '—';
    const ms = Math.max(0, Date.now() - new Date(entered).getTime());
    return formatDuration(ms);
  };

  const getSlaDue = (item: any) => {
    const fallbackDueMs = new Date(item.created_at).getTime() + 12 * 60 * 60 * 1000; // 12h fallback
    const dueTs = item.sla_due_at ? new Date(item.sla_due_at).getTime() : fallbackDueMs;
    const diff = dueTs - Date.now();
    const ms = Math.abs(diff);
    const label = formatDuration(ms);
    return diff >= 0 ? `${label}` : `-${label}`;
  };

  // Queue items are now filtered in the hook for better performance and consistency

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (checked) {
      setSelectedItems(prev => [...prev, itemId]);
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    }
  };

  const handleSelectAll = (items: any[], checked: boolean) => {
    const itemIds = items.map(item => item.id);
    if (checked) {
      setSelectedItems(prev => [...new Set([...prev, ...itemIds])]);
    } else {
      setSelectedItems(prev => prev.filter(id => !itemIds.includes(id)));
    }
  };

  const handleBulkProcessing = async () => {
    if (selectedItems.length === 0) return;

    setIsProcessing(true);
    try {
      for (const itemId of selectedItems) {
        await processQueueItem(itemId);
      }

      toast({
        title: "Bulk Processing Started",
        description: `Processing ${selectedItems.length} items`,
      });

      setSelectedItems([]);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to start bulk processing",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkAssignment = async (accountId: string) => {
    if (selectedItems.length === 0) return;

    try {
      for (const itemId of selectedItems) {
        await assignToBill({ id: itemId, data: { assigned_to_account_id: accountId } });
      }

      toast({
        title: "Bulk Assignment Complete",
        description: `Assigned ${selectedItems.length} items to account`,
      });

      setSelectedItems([]);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to assign items",
        variant: "destructive",
      });
    }
  };

  const getProcessingStageIcon = (stage: string) => {
    switch (stage) {
      case 'Intake Hub': return <Upload className="h-4 w-4" />;
      case 'Data Extraction': return <Search className="h-4 w-4" />;
      case 'Processing': return <RefreshCw className="h-4 w-4" />;
      case 'Manual Review': return <User className="h-4 w-4" />;
      case 'Validation': return <Eye className="h-4 w-4" />;
      case 'Approval': return <CheckSquare className="h-4 w-4" />;
      case 'Payment Ready': return <DollarSign className="h-4 w-4" />;
      case 'Archive': return <Archive className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getProcessingStageColor = (stage: string) => {
    switch (stage) {
      case 'Intake Hub': return 'default';
      case 'Data Extraction': return 'secondary';
      case 'Processing': return 'default';
      case 'Manual Review': return 'secondary';
      case 'Validation': return 'outline';
      case 'Approval': return 'default';
      case 'Payment Ready': return 'secondary';
      case 'Archive': return 'outline';
      default: return 'outline';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'default';
    if (confidence >= 0.7) return 'secondary';
    return 'destructive';
  };

  // Helper function to generate Bill ID
  const generateBillId = (item: any) => {
    const supplierName = item.supplier_name || 'UNK';
    const prefix = supplierName.substring(0, 3).toUpperCase();
    const year = new Date(item.created_at).getFullYear();
    const sequence = item.id.substring(0, 8).toUpperCase();
    return `${prefix}-${year}-${sequence}`;
  };

  // Helper function to handle PDF click
  const handlePdfClick = (item: any) => {
    if (item) {
      setPreviewItem(item);
      setPreviewOpen(true);
      return;
    }
    toast({
      title: "Preview not available",
      description: "No file found for this item",
      variant: "destructive"
    });
  };

  // Helper function to extract data with fallback logic
  const getDisplayValue = (item: any, field: string) => {
    const extractedData = item.extracted_data || {};
    
    switch (field) {
      case 'customer_name':
        return extractedData.customer_name || item.customer_name_raw || 'Not detected';
      case 'account_number':
        return extractedData.account_number || item.account_number_raw || 'Not detected';
      case 'amount':
        const amount = extractedData.amount || item.amount;
        return amount ? `$${parseFloat(amount).toFixed(2)}` : 'Not detected';
      default:
        return 'Not detected';
    }
  };

  const renderQueueTable = (items: any[], title: string) => {
    // Enhanced queue-specific columns with better data mapping
    const getQueueColumns = (queueType: string) => {
      switch (queueType) {
        case 'Intake Hub':
          return ['Source', 'Customer', 'Retailer', 'Account/Site', 'Period', 'Amount', 'Flags', 'Assignee'];
        case 'Data Extraction':
          return ['Customer', 'Account', 'Amount', 'Confidence%', 'AI Status'];
        case 'Processing':
          return ['Customer', 'Account', 'Amount', 'Service Type', 'Auto-Assignment'];
        case 'Manual Review':
          return ['Customer', 'Account', 'Amount', 'Issues', 'Priority'];
        case 'Validation':
          return ['Customer', 'Account', 'Amount', 'Validation Status', 'Issues Count'];
        case 'Approval':
          return ['Customer', 'Account', 'Amount', 'Reviewer', 'Due Date'];
        case 'Payment Ready':
          return ['Customer', 'Account', 'Amount', 'Due Date', 'Payment Status'];
        default:
          return ['Customer Name', 'Account Number', 'Amount', 'Confidence', 'Status'];
      }
    };

    // Queue-specific color themes
    const getQueueTheme = (queueType: string) => {
      switch (queueType) {
        case 'Intake Hub': return 'border-l-blue-500';
        case 'Data Extraction': return 'border-l-yellow-500';
        case 'Processing': return 'border-l-purple-500';
        case 'Manual Review': return 'border-l-orange-500';
        case 'Validation': return 'border-l-cyan-500';
        case 'Approval': return 'border-l-green-500';
        case 'Payment Ready': return 'border-l-emerald-500';
        case 'Archive': return 'border-l-gray-500';
        default: return 'border-l-gray-300';
      }
    };

    const queueColumns = getQueueColumns(title);

    return (
        <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {getProcessingStageIcon(title)}
            {title}
            <Badge variant="outline">{items.length}</Badge>
          </h3>
          {items.length > 0 && (
            <div className="flex items-center justify-between flex-1 ml-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={items.every(item => selectedItems.includes(item.id))}
                  onCheckedChange={(checked) => handleSelectAll(items, !!checked)}
                />
                <span className="text-sm text-muted-foreground">Select All</span>
              </div>
              
              {/* Column Settings Button */}
              <ColumnSettings 
                columns={columns} 
                onColumnsChange={setColumns} 
              />
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <Card>
            <CardContent className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">No items in this queue</p>
            </CardContent>
          </Card>
        ) : (
          <Card className={`border-l-4 ${getQueueTheme(title)}`}>
            <Table>
              <TableHeader>
                <TableRow>
                  {isColumnVisible('checkbox') && <TableHead className="w-12"></TableHead>}
                  {isColumnVisible('expand') && <TableHead className="w-12"></TableHead>}
                  {isColumnVisible('files') && <TableHead>Files</TableHead>}
                  {isColumnVisible('bill_id') && <TableHead>Bill ID</TableHead>}
                  {isColumnVisible('stage_age') && <TableHead>Stage Age</TableHead>}
                  {isColumnVisible('sla_due') && <TableHead>SLA Due</TableHead>}
                  {isColumnVisible('parsing_method') && title === 'Processing' && <TableHead>Parse Method</TableHead>}
                  {isColumnVisible('parsing_status') && title === 'Processing' && <TableHead>Parse Status</TableHead>}
                  {queueColumns.map((column, idx) => {
                    // Map dynamic columns to column IDs
                    const columnId = column.toLowerCase().replace(/[^a-z0-9]+/g, '_');
                    const isVisible = isColumnVisible(columnId) || 
                      (columnId.includes('customer') && isColumnVisible('customer')) ||
                      (columnId.includes('account') && isColumnVisible('account')) ||
                      (columnId.includes('amount') && isColumnVisible('amount')) ||
                      (columnId.includes('confidence') && isColumnVisible('confidence')) ||
                      true; // Show unmapped columns by default
                    return isVisible ? <TableHead key={idx}>{column}</TableHead> : null;
                  })}
                  {isColumnVisible('actions') && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const isExpanded = expandedRows.has(item.id);
                  return (
                    <>
                      <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50">
                        {isColumnVisible('checkbox') && (
                          <TableCell>
                            <Checkbox
                              checked={selectedItems.includes(item.id)}
                              onCheckedChange={(checked) => handleSelectItem(item.id, !!checked)}
                            />
                          </TableCell>
                        )}
                        {isColumnVisible('expand') && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newExpanded = new Set(expandedRows);
                                if (isExpanded) newExpanded.delete(item.id);
                                else newExpanded.add(item.id);
                                setExpandedRows(newExpanded);
                              }}
                            >
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          </TableCell>
                        )}
                        {isColumnVisible('files') && (<TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <div 
                                className="cursor-pointer hover:text-primary transition-colors flex items-center gap-1"
                                onClick={() => handlePdfClick(item)}
                                title={`View ${item.mime_type?.includes('pdf') ? 'PDF' : 'File'}`}
                              >
                                <FileText className="h-4 w-4" />
                                <span className="text-xs text-muted-foreground">
                                  {item.mime_type?.includes('pdf') ? 'PDF' : 'DOC'}
                                </span>
                              </div>
                              {item.mime_type?.includes('csv') && (
                                <div 
                                  className="cursor-pointer hover:text-primary transition-colors"
                                  title="CSV File"
                                >
                                  <File className="h-4 w-4" />
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        )}
                        {isColumnVisible('bill_id') && (<TableCell className="font-medium">
                          <span className="truncate max-w-32 font-mono text-sm">
                            {generateBillId(item)}
                          </span>
                        </TableCell>
                        )}
                        {isColumnVisible('stage_age') && <TableCell>{getStageAge(item)}</TableCell>}
                        {isColumnVisible('sla_due') && <TableCell>{getSlaDue(item)}</TableCell>}
                        {isColumnVisible('parsing_method') && title === 'Processing' && (
                          <TableCell>
                            <ParsingMethodBadge method={item.parsing_method} />
                          </TableCell>
                        )}
                        {isColumnVisible('parsing_status') && title === 'Processing' && (
                          <TableCell>
                            <ParsingStatusBadge status={item.parsing_status} />
                          </TableCell>
                        )}
                    {title === 'Intake Hub' && (
                      <>
                        <TableCell>{item.source_type || 'manual'}</TableCell>
                        <TableCell>
                          {item.assigned_to_account_id ? (
                            <div className="space-y-1">
                              <div className="font-medium">{getDisplayValue(item, 'customer_name')}</div>
                              {item.customer_confidence && (
                                <Badge variant={getConfidenceColor(item.customer_confidence)} className="text-xs">
                                  {(item.customer_confidence * 100).toFixed(0)}%
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <Badge variant="outline">Unassigned</Badge>
                          )}
                        </TableCell>
                        <TableCell>{item.supplier_name || 'Unknown'}</TableCell>
                        <TableCell>{getDisplayValue(item, 'account_number')}</TableCell>
                        <TableCell>
                          {item.bill_date
                            ? new Date(item.bill_date).toLocaleDateString()
                            : (item.extracted_data?.billing_period_start && item.extracted_data?.billing_period_end)
                              ? `${new Date(item.extracted_data.billing_period_start).toLocaleDateString()} - ${new Date(item.extracted_data.billing_period_end).toLocaleDateString()}`
                              : '—'}
                        </TableCell>
                        <TableCell>{getDisplayValue(item, 'amount')}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {item.is_duplicate && <Badge variant="destructive">Duplicate</Badge>}
                            {item.is_readable === false && <Badge variant="secondary">Unreadable</Badge>}
                            {!item.assigned_to_account_id && <Badge variant="outline">Unassigned</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {item.assigned_by ? 'Assigned' : '—'}
                          </span>
                        </TableCell>
                      </>
                    )}
                    {title !== 'Intake Hub' && (
                      <>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{getDisplayValue(item, 'customer_name')}</div>
                            {item.customer_confidence && (
                              <Badge variant={getConfidenceColor(item.customer_confidence)} className="text-xs">
                                {(item.customer_confidence * 100).toFixed(0)}%
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{getDisplayValue(item, 'account_number')}</div>
                            {item.account_confidence && (
                              <Badge variant={getConfidenceColor(item.account_confidence)} className="text-xs">
                                {(item.account_confidence * 100).toFixed(0)}%
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {getDisplayValue(item, 'amount')}
                        </TableCell>
                        {title === 'Data Extraction' && (
                          <TableCell>
                            <Badge variant={getConfidenceColor(item.ai_confidence || 0)} className="text-xs">
                              AI: {((item.ai_confidence || 0) * 100).toFixed(0)}%
                            </Badge>
                          </TableCell>
                        )}
                        {title === 'Validation' && (
                          <TableCell>
                            <Badge variant="secondary">
                              {item.validation_errors?.length || 0} issues
                            </Badge>
                          </TableCell>
                        )}
                        {title === 'Approval' && (
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {item.assigned_by ? 'Pending' : 'Unassigned'}
                            </span>
                          </TableCell>
                        )}
                        {title === 'Payment Ready' && (
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {item.bill_date ? new Date(item.bill_date).toLocaleDateString() : 'TBD'}
                            </span>
                          </TableCell>
                        )}
                      </>
                    )}
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {title === 'Processing' && item.parsing_status === 'waiting_trigger' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    supabase.functions.invoke('multi-tier-bill-parser', { 
                                      body: { bill_id: item.bill_id } 
                                    });
                                    toast({ title: '✨ AI Parsing started' });
                                  }}>
                                    <Brain className="h-4 w-4 mr-2" />
                                    Trigger AI Parsing
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    toast({ title: 'Moving to manual review' });
                                  }}>
                                    <Hand className="h-4 w-4 mr-2" />
                                    Send to Manual Review
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handlePdfClick(item)}>
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            {title === 'Processing' && item.parsing_status === 'api_failed' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    supabase.functions.invoke('bill-parsing-orchestrator', { 
                                      body: { bill_id: item.bill_id } 
                                    });
                                    toast({ title: '🔄 Retrying Vendor API' });
                                  }}>
                                    <RotateCw className="h-4 w-4 mr-2" />
                                    Retry Vendor API
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    supabase.functions.invoke('multi-tier-bill-parser', { 
                                      body: { bill_id: item.bill_id } 
                                    });
                                    toast({ title: '🤖 Trying AI Parsing' });
                                  }}>
                                    <Brain className="h-4 w-4 mr-2" />
                                    Try AI Parsing
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    toast({ title: 'Moving to manual review' });
                                  }}>
                                    <Hand className="h-4 w-4 mr-2" />
                                    Send to Manual Review
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            {(title === 'Processing' && ['ai_success', 'ai_failed', 'api_success'].includes(item.parsing_status || '')) && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setReparseDialog({ open: true, billId: item.bill_id, data: item.extracted_data })}
                                  title="Re-parse with AI"
                                >
                                  <Sparkles className="h-4 w-4" />
                                </Button>
                                {item.extracted_data && (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => setAssignmentDialog({ open: true, queueItem: item })}
                                    title="Create Bill from extracted data"
                                  >
                                    <FileCheck className="h-4 w-4 mr-1" />
                                    Create Bill
                                  </Button>
                                )}
                              </>
                            )}
                        {title === 'Intake Hub' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setAssignmentDialog({ open: true, queueItem: item })}
                            >
                              Assign
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => processQueueItem(item.id)}
                            >
                              Move → Data Extraction
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                const target = window.prompt('Original bill/queue ID (optional)')?.trim();
                                try {
                                  await updateQueueItem({
                                    id: item.id,
                                    data: {
                                      is_duplicate: true,
                                      dup_of_queue_id: target || undefined,
                                      flags: Array.from(new Set([...(item.flags || []), 'duplicate']))
                                    }
                                  });
                                  toast({ title: 'Marked duplicate', description: 'Item flagged as duplicate.' });
                                } catch (e: any) {
                                  toast({ title: 'Failed', description: e.message || 'Could not mark duplicate', variant: 'destructive' });
                                }
                              }}
                            >
                              Mark Duplicate
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                const note = window.prompt('Add note');
                                if (!note) return;
                                try {
                                  await updateQueueItem({ id: item.id, data: { notes: [item.notes, note].filter(Boolean).join(' | ') } });
                                  toast({ title: 'Note added', description: 'Your note was saved.' });
                                } catch (e: any) {
                                  toast({ title: 'Failed', description: e.message || 'Could not add note', variant: 'destructive' });
                                }
                              }}
                            >
                              Add Note
                            </Button>
                          </>
                        )}
                        {title === 'Data Extraction' && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => processQueueItem(item.id)}
                            >
                              Process
                            </Button>
                            {item.processing_status === 'completed' && item.extracted_data && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => setAssignmentDialog({ open: true, queueItem: item })}
                              >
                                Create Bill
                              </Button>
                            )}
                          </div>
                        )}
                        {title === 'Validation' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAssignmentDialog({ open: true, queueItem: item })}
                          >
                            Validate
                          </Button>
                        )}
                        {title === 'Approval' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline">
                              Approve
                            </Button>
                            <Button size="sm" variant="ghost">
                              Reject
                            </Button>
                          </div>
                        )}
                        {title === 'Payment Ready' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => createBillFromQueue(item.id, item.assigned_to_account_id)}
                          >
                            Process Payment
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePdfClick(item)}
                        >
                          View
                        </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Expanded Row Content */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={columns.filter(c => c.visible).length}>
                          <ExpandedRowContent 
                            item={item}
                            onReparse={() => setReparseDialog({ open: true, billId: item.bill_id, data: item.extracted_data })}
                            onAccept={() => {
                              toast({ title: 'Bill accepted', description: 'Moved to validation stage' });
                            }}
                            onManualReview={() => {
                              toast({ title: 'Sent to manual review' });
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        Loading queue...
      </div>
    );
  }

  // Filter items based on active tab
  const getFilteredItems = () => {
    switch (activeTab) {
      case 'intake':
        return intakeItems;
      case 'extraction':
        return dataExtractionItems;
      case 'processing':
        return processingItems;
      case 'review':
        return manualReviewItems;
      case 'validation':
        return validationItems;
      case 'approval':
        return approvalItems;
      case 'payment':
        return paymentReadyItems;
      case 'archive':
        return archiveItems;
      default:
        return intakeItems;
    }
  };

  // Get display title for active tab
  const getTabTitle = (tab: string) => {
    switch (tab) {
      case 'intake': return 'Intake Hub';
      case 'extraction': return 'Data Extraction';
      case 'processing': return 'Processing';
      case 'review': return 'Manual Review';
      case 'validation': return 'Validation';
      case 'approval': return 'Approval';
      case 'payment': return 'Payment Ready';
      case 'archive': return 'Archive';
      default: return 'Intake Hub';
    }
  };

  return (
    <div className="space-y-6">
      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Bulk Actions</CardTitle>
            <CardDescription>
              {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleBulkProcessing}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                Process Selected
              </Button>
              <Button 
                variant="outline" 
                onClick={async () => {
                  const accountId = window.prompt('Account ID to assign to selected items');
                  if (!accountId) return;
                  await handleBulkAssignment(accountId);
                }}
              >
                Assign to Account
              </Button>
              <Button 
                variant="outline" 
                onClick={async () => {
                  const note = window.prompt('Note to add to selected items');
                  if (!note) return;
                  for (const id of selectedItems) {
                    await updateQueueItem({ id, data: { notes: note } });
                  }
                  toast({ title: 'Notes added', description: 'Note added to selected items.' });
                }}
              >
                Add Note
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setSelectedItems([])}
              >
                Clear Selection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Queue Content */}
      {activeTab === 'archive' ? (
        <ArchiveHierarchyView />
      ) : (
        renderQueueTable(getFilteredItems(), getTabTitle(activeTab))
      )}

      <AILearningFeedbackDialog
        open={feedbackDialog.open}
        onOpenChange={(open) => setFeedbackDialog({ ...feedbackDialog, open })}
        extractedData={feedbackDialog.data || {}}
        billId={feedbackDialog.billId}
        queueItemId={feedbackDialog.queueItemId}
        onFeedbackSubmitted={() => {
          setFeedbackDialog({ open: false });
          // Optionally refresh the queue data
        }}
      />

      <QueueAssignmentDialog
        open={assignmentDialog.open}
        onOpenChange={(open) => setAssignmentDialog({ ...assignmentDialog, open })}
        queueItem={assignmentDialog.queueItem}
        onAssign={createBillFromQueue}
      />

      <BillPreviewDrawer
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        item={previewItem}
      />
      <ReparseDialog
        open={reparseDialog.open}
        onOpenChange={(open) => setReparseDialog({ ...reparseDialog, open })}
        billId={reparseDialog.billId || ''}
        currentData={reparseDialog.data}
      />
    </div>
  );
};